import { BookOpenCheck, Calendar, KeyRound, Users } from 'lucide-react';
import CoachStudioActionTile from './CoachStudioActionTile';
import type { CoachStudioHeaderProps } from './coachStudioTypes';
import { formatProviderWorkflowState } from './coachStudioUtils';

export default function CoachStudioHeader({
  displayName,
  roleLabel,
  workTabLabel,
  directoryLabel,
  accessLabel,
  occurrenceCount,
  programCount,
  linkedAthleteCount,
  activeJoinCodeCount,
  hasStudents,
  hasSessions,
  hasAccessStarted,
  provider,
  coachDashboard,
  onOpenCatalog,
  onOpenWork,
  onOpenDirectory,
  onOpenAccess,
}: CoachStudioHeaderProps) {
  const listingLabel = provider?.hasProfile
    ? provider.publiclyVisible
      ? 'Public Profile Live'
      : `Public Profile ${formatProviderWorkflowState(provider.workflowState)}`
    : null;
  const primaryActionLabel = programCount > 0 ? workTabLabel : 'Classes';
  const primaryActionValue = programCount > 0 ? (hasSessions ? 'Open day' : 'Create session') : 'Open catalog';

  return (
    <section className="rounded-lg border border-border bg-white p-4 shadow-[var(--shadow-card)] md:p-5">
      <div className="flex flex-col gap-5 xl:flex-row xl:items-center xl:justify-between">
        <div className="min-w-0">
          <p className="text-[11px] font-semibold uppercase tracking-[0.18em] text-primary">Coach Studio</p>
          <h1 className="mt-2 truncate text-3xl font-semibold tracking-tight text-text sm:text-4xl">{displayName}</h1>
          <div className="mt-3 flex flex-wrap gap-2">
            <CoachStatusPill label={roleLabel} />
            {programCount > 0 ? <CoachStatusPill label={`${programCount} classes`} /> : null}
            {hasStudents ? <CoachStatusPill label={`${linkedAthleteCount} students`} /> : null}
            {hasSessions ? <CoachStatusPill label={`${occurrenceCount} sessions`} /> : null}
            {hasAccessStarted ? <CoachStatusPill label={`${activeJoinCodeCount} join codes`} /> : null}
            {listingLabel ? <CoachStatusPill label={listingLabel} tone="live" /> : null}
          </div>
        </div>

        <div className="grid min-w-0 gap-3 sm:grid-cols-3 xl:w-[560px]">
          <CoachStudioActionTile
            label={primaryActionLabel}
            value={primaryActionValue}
            icon={programCount > 0 ? Calendar : BookOpenCheck}
            onClick={programCount > 0 ? onOpenWork : onOpenCatalog}
            emphasis={programCount === 0 || !hasSessions || (coachDashboard?.attentionItems.length ?? 0) > 0}
          />
          <CoachStudioActionTile
            label={directoryLabel}
            value={hasStudents ? 'View roster' : 'Add student'}
            icon={Users}
            onClick={onOpenDirectory}
            emphasis={!hasStudents}
          />
          <CoachStudioActionTile
            label={accessLabel}
            value={hasAccessStarted ? 'Family access' : 'Create code'}
            icon={KeyRound}
            onClick={onOpenAccess}
            emphasis={!hasAccessStarted}
          />
        </div>
      </div>
    </section>
  );
}

function CoachStatusPill({ label, tone = 'default' }: { label: string; tone?: 'default' | 'live' }) {
  return (
    <span
      className={
        tone === 'live'
          ? 'rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-primary'
          : 'rounded-full border border-border bg-surface-elevated/30 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.14em] text-text-subtle'
      }
    >
      {label}
    </span>
  );
}
